import { AppDate } from '../envTypes';

import {
  CommissionFeeType,
  ModelBase,
  Platform,
  RequireRedaction,
  ShippingFeePayer,
  ShippingIntegrationType,
  WithID,
} from './base';
import { Gift, LiveStream, SendItem, SendItemShowSource } from './livestream';
import { Profile } from './profile';
import { Address, PickupAddress } from './address';
import { PaymentType } from './payment';
import {
  USPSShippingServiceLevel,
  SignatureConfirmationType,
  PackageTemplate,
  ShippoRate,
  Shipment,
} from './shipping';
import type { StoreFront } from './store';
import { Listing, Tip } from './listing';
import { GroupEventLocationType } from './group-event';
import { UpcomingShow } from './upcoming-show';
import { SentReferralGift } from './referral-gifting';
import { Coupon, CouponType } from './coupon';
import { TokenListing } from './token';

export enum MsmStatus {
  approved = 'approved',
  deny = 'deny',
}

export enum CreateOrderSourceType {
  UpcomingShow = 'UPCOMING_SHOW',
  LiveStream = 'LIVE_STREAM',
  Store = 'STORE',
  TwoWayStream = 'TWO_WAY_STREAM',
  ReferralGifting = 'REFERRAL_GIFTING',
  SendItem = 'SEND_ITEM',
  TopProducts = 'TOP_PRODUCTS',
  Shop = 'SHOP',
  Replay = 'REPLAY',
  CommentChat = 'COMMENT_CHAT',
}

export type CreateOrderSource =
  | {
      type: CreateOrderSourceType.UpcomingShow;
      upcomingShowId: string;
    }
  | { type: CreateOrderSourceType.LiveStream; liveStreamId: string }
  | {
      type: CreateOrderSourceType.Store;
      storeId: string;
      liveStreamId?: string;
    }
  | {
      type: CreateOrderSourceType.TwoWayStream;
      liveStreamId: string;
    }
  | {
      type: CreateOrderSourceType.ReferralGifting;
      sentReferralGiftId: string;
    }
  | {
      type: CreateOrderSourceType.SendItem;
      sendItemId: string;
      source:
        | { type: SendItemShowSource.liveStream; liveStreamId: string }
        | { type: SendItemShowSource.upcomingShow; upcomingShowId: string };
    }
  | { type: CreateOrderSourceType.TopProducts }
  | { type: CreateOrderSourceType.Replay; liveStreamId: string }
  | {
      type: CreateOrderSourceType.CommentChat;
      storeId: string;
      postLinkId: string;
      channelId?: string;
    }
  | { type: CreateOrderSourceType.Shop };

// OrderShippingMethod has to be after CouponCreateMethod
// otherwise it will not get generated on Swift side
export type OrderShippingMethod =
  | 'online'
  | 'sellerProvide'
  | 'popshopProvide'
  | 'pickup'
  | 'dropshipSupplierProvide';

export interface ComputeOrder {
  listingId: string;
  sellerId: string;
  sendItemId?: string;
  liveStreamId?: string;
  variantId?: string;
  shippingAddress: Address;
  pickupAddress?: OrderPickupAddress;
  redeemedCouponIds?: string[];
  /** @TJS-type integer */
  quantity: number;
  shippingMethod?: OrderShippingMethod;
  source?: CreateOrderSource;
  recipientId?: string;
  upcomingShowId?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  utmParameters?: Record<string, any>;
}

export interface ComputeOrderOutput {
  buyerPaidAmount: number;
  sellerGotAmount: number;
  newBonusBalance: number;
  shippingMethod: string;
  total: number;
  bonus: number;
  salesTaxAmount: number;
  salesTaxRate: number;
  shippingFee: number;
  couponDiscountShippingFeeAmount: number;
  couponDiscountProductAmount: number;
  couponSellerGotAmount: number;
}

export interface CreateOrderV2 extends ComputeOrder {
  paymentMethodId: string;
  paymentType: PaymentType;
  buyerName: string;
  useBonus?: boolean;
  requestTrackingID: string;
  userAgent?: string;
  giftPackage?: {
    isGiftPackage: boolean;
    giftMessage?: string;
  };
}

export interface CreateOrder extends ComputeOrder {
  nonce?: string;
  creditCardId?: string;
  paymentType: PaymentType;
  buyerName: string;
  useBonus?: boolean;
  requestTrackingID: string;
  userAgent?: string;
  giftPackage?: {
    isGiftPackage: boolean;
    giftMessage?: string;
  };
}

export interface CreateTicketOrder
  extends Pick<
    CreateOrder,
    'nonce' | 'creditCardId' | 'paymentType' | 'buyerName' | 'userAgent'
  > {
  shippingAddress?: Address;
  eventId: string;
  ticketId: string;
}

export interface CreateTicketOrderV2
  extends Pick<
    CreateOrderV2,
    'paymentMethodId' | 'paymentType' | 'buyerName' | 'userAgent'
  > {
  shippingAddress?: Address;
  eventId: string;
  ticketId: string;
}

export enum OrderStatus {
  Created = 'created',
  Completed = 'completed',
  Canceled = 'canceled',
  /**
   * haven't paid order
   */
  PendingPayment = 'pendingPayment',
  /**
   * payment is being processed, but waiting for Stripe or bank confirmation or need further action
   */
  ProcessingPayment = 'processingPayment',
  /**
   * When order hasn't pay in limit time, it will be closed by system
   */
  Closed = 'closed',
}

export enum OrderGroupStatus {
  Open = 'open',
  Processing = 'processing',
  Fulfilling = 'fulfilling',
  Completed = 'completed',
  Canceled = 'canceled',
}

export interface SellerTrackingInfo {
  carrier?: string;
  trackingInfo: string; // tracking number
  trackingURL?: string;
  createdAt: AppDate;
  voided?: boolean;
}

export type OrderPickupAddress = { addressId: string } & Omit<
  PickupAddress,
  'isDefault' | 'createdAt' | 'updatedAt'
>;

export enum OrderPickupStatus {
  Open = 'open',
  New = 'new',
  Processing = 'processing',
  // master order only
  Pending = 'pending',
  Packed = 'packed',
  Completed = 'completed',
  Canceled = 'canceled',
}

export enum OrderCancelStatus {
  SellerApplied = 'seller_applied',
  AdminApproved = 'admin_approved',
}

export enum GiftingStatus {
  Pending = 'PENDING',
  Accepted = 'ACCEPTED',
  Rejected = 'REJECTED',
}

/**
 * @deprecated since 12/06/22
 */
export interface OrderShipmentConfig {
  templateId?: string;
  templateSnapshot?: PackageTemplate;
  parcel?: OrderShipmentParcel | ShipmentTemplateParcel;
  selectedRateId?: {
    default: string;
    userSelected: string;
  };
  serviceLevel?:
    | USPSShippingServiceLevel.USPS_PARCEL_SELECT
    | USPSShippingServiceLevel.USPS_FIRST_CLASS
    | USPSShippingServiceLevel.USPS_PRIORITY
    | USPSShippingServiceLevel.USPS_PRIORITY_EXPRESS;
  rateAttributes?:
    | USPSShippingServiceLevel.CHEAPEST_AVAILABLE
    | USPSShippingServiceLevel.FASTEST_AVAILABLE;
  extra?: { signatureConfirmation?: SignatureConfirmationType };
}

export enum OrderRefundStatus {
  PartiallyRefunded = 'PARTIALLY_REFUNDED',
  FullyRefunded = 'FULLY_REFUNDED',
}

export enum OrderGroupCancelStatus {
  PartiallyCanceled = 'PARTIAL_CANCELED',
  FullyCanceled = 'FULLY_CANCELED',
}

export interface DropshipPartner {
  id: number;
  name: string;
}

export interface DropshipSupplier {
  name: string;
  acceptReturn?: boolean; // default true
  freeShippingThreshold?: number; // default undefined
  supportedForeignCountries?: string[]; // default undefined; value should be country code
}

export interface DropshipDetails {
  isDropship: boolean;
  partnerId: number;
  partnerName: string;
  supplierId: number;
  supplierName: string;
  commentSoldProductId: number;
  commentSoldVariantId: number;
}

export interface DropshipAmounts {
  costAmount: number;
  soldAmount: number;
  subtotalPaidByPopshop?: number; // PSL provided listing coupon
  subtotalPaidBySeller?: number; // seller provided listing coupon
  managementRate: number; // 5%
  managementFee: number; // soldAmount * managementRate
  cumulativeListingDiscountedSubTotal: number;
  cumulativeBuyerPaidShippingFee: number;
  cumulativeBuyerPaidShippingSalesTaxAmount: number;
  freeShippingThreshold?: number;
  freeShippingDiff?: number;
  freeShippingDiscountAmount?: number;
  qualifiedFreeShipping?: boolean;
  toBeQualifiedFreeShipping?: boolean;
  shippingFee: number;
  shippingFeePaidByPopshop?: number;
  shippingFeePaidBySeller?: number;
  supplierWithdrawableAmount: number;
}

export interface MultiCurrencyOrderDetails {
  currency: string;
  exchangeRate: number;
  buyerPaidAmount: number;
  shippingFee: number;
  shippingFeeAdjusted: number;
  salesTaxRate?: number;
  salesTaxAmount?: number;
  total: number;
  totalPriceIncludeSlaveOrders: number;
  totalPriceIncludeSlaveOrdersWithProductCoupon: number;
  totalRefundIncludeSlaveOrders?: number;
  refund?: number;
  couponDiscountProductAmount?: number;
  couponDiscountShippingFeeAmount?: number;
  sellerGotAmount: number;
  bonus?: number;
  orderChargeDistribution?: OrderChargeDistribution | null;
  refunds?: RefundRecord[];
  priceComponents?: OrderPriceComponents;
}

export interface BaseOrder {
  // default to Platform.PopshopLive if not specified
  platform?: Platform;
  eventId?: string;
  /**
   * @deprecated
   */
  buyerName: string;
  orderNumber: string;
  /** @TJS-type integer */
  quantity: number;
  buyerPaidAmount: number;
  shippingFee: number;
  shippingFeeAdjusted: number;
  braintreeShippingFeeRefunded?: boolean;
  salesTaxRate?: number;
  salesTaxAmount?: number;
  total: number;
  buyerId: string;
  buyer: Profile & RequireRedaction;
  shipment?: Shipment;
  packingSlip?: PackingSlip;
  refund?: number;
  createdAt: AppDate;
  userAgent?: string;
  /**
   * @deprecated
   */
  orderPayment?: OrderPayment;
  /**
   * @instead of orderPayment exist if buyerPaidAmount > 0
   */
  payment?: Payment;
  coupons?: {
    redeemedCouponId: string;
    couponId: string;
    coupon: Coupon;
    fromHistory?: boolean;
  }[];
  couponDiscountProductAmount?: number;
  couponDiscountShippingFeeAmount?: number;
  sellerTrackingInfo?: SellerTrackingInfo;
  sellerTrackingInfoHistory?: SellerTrackingInfo[];
  balance?: {
    availableAt: AppDate;
    availableAmount: number;
  };
}

// /orders/{id}
export interface Order extends BaseOrder {
  sellerGotAmount: number;
  bonus?: number;
  shippingAddress: Address;
  pickupAddress?: OrderPickupAddress;
  listingId: string;
  listingSnapshot: Listing;
  sendItemId?: string;
  sendItem?: SendItem;
  sentReferralGiftId?: string;
  sentReferralGift?: SentReferralGift;
  sellerId: string;
  seller: Profile;
  dropshipDetails?: DropshipDetails;

  gifting?: {
    recipientId: string;
    recipient: Profile & { nftImageUrl?: string };
    status: GiftingStatus;
  };

  status: OrderStatus;
  orderGroupStatus?: OrderGroupStatus;
  orderGroupClosedAt?: AppDate;
  orderRefundStatus?: OrderRefundStatus;
  orderGroupRefundStatus?: OrderRefundStatus;
  orderGroupCancelStatus?: OrderGroupCancelStatus;
  pickupStatus?: OrderPickupStatus;
  pickupNotifiedAt?: AppDate;
  cancelStatus?: OrderCancelStatus;
  shippingMethod: OrderShippingMethod;
  shippoOrderId?: string;
  liveStreamId?: string | null;
  variantId?: string | null;
  giftIds?: string[];
  /** @ignore */
  gifts?: Gift[];
  store: StoreFront;

  orderShipmentGroupStatus: {
    master: boolean;
    slave: boolean;
    customer: boolean;
    legacy: boolean;
  };

  shippingLabelStatus: {
    disabled: boolean;
    noLabel: boolean;
    generating: boolean;
    generated: boolean;
    preGenerated: boolean;
    failed: boolean;
  };

  packingSlipStatus: {
    disabled: boolean;
    noLabel: boolean;
    generating: boolean;
    generated: boolean;
    preGenerated: boolean;
    failed: boolean;
  };

  masterOrderId?: string;
  planTimeOfShippingLabelGeneration?: AppDate;
  /** @TJS-type integer */
  totalQuantitiesIncludeSlaveOrders: number;
  totalPriceIncludeSlaveOrders: number;
  totalPriceIncludeSlaveOrdersWithProductCoupon: number;
  totalRefundIncludeSlaveOrders?: number;
  newCustomer?: boolean;
  firstPurchaseAt?: AppDate;

  orderCompositionCancellationStatus?: {
    allCancelled: boolean;
    noneCancelled: boolean;
    partiallyCancelled: boolean;
  };
  orderChargeDistribution?: OrderChargeDistribution | null;

  packed?: boolean;
  upcomingShowId?: string;
  giftPackage?: {
    isGiftPackage: boolean;
    giftMessage?: string;
  };
  source?: CreateOrderSource;
  priceComponents?: OrderPriceComponents;
  input?: CreateOrder;
  shipmentParcel?: ShipmentParcel;
  shipmentOriginalParcel?: ShipmentParcel;
  shipmentOriginalParcelSource?: ShipmentOriginalParcelSource;
  refunds?: RefundRecord[];
  /**
   * @deprecated since 12/06/22
   */
  shipmentConfig?: OrderShipmentConfig;
  packageTemplateId?: string;
  packageTemplate?: PackageTemplate;
  presetShippingCost?: ShippingCost;

  isPslPayOrder?: boolean;

  commentSold?:
    | {
        shopId?: string;
        orderId: number;
        syncAt: AppDate;
        success: true;
        subtotalAdjusted?: boolean;
      }
    | {
        success: false;
        error: string;
      };

  fundAvailableDate?: AppDate;
  fundAvailableDate2?: AppDate;
  labelShippingCost?: {
    initialCost?: number;
    initialDate?: AppDate;
    totalCost?: number;
    lastUpdatedAt?: AppDate;
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  utmParameters?: Record<string, any>;

  calendarEvent?: {
    eventId: string;
    calendarId: string;
    meetingUrl?: string;
    rescheduleCount?: number;
  };
  groupEvent?: {
    groupEventId: string;
    name: string;
    startDate: AppDate;
    endDate: AppDate;
    timezone: string;
    locationType: GroupEventLocationType;
    address?: string;
  };
  creatorCurrencyOrderDetails?: MultiCurrencyOrderDetails;
  buyerCurrencyOrderDetails?: MultiCurrencyOrderDetails;
  paymentExpireAt?: AppDate;
  crowdTipping?: {
    accumulated?: boolean;
  };
  tokenListingSnapshot?: WithID<TokenListing>;
}

export interface BraintreeTransaction {
  id: string;
  status: string;
  type: string;
  amount: string;
  refundIds: string[];
  refundedTransactionId?: null | string;
  createdAt: string;
  updatedAt: string;
  disputes?: BraintreeDispute[];
  disbursementDetails?: BraintreeDisbursementDetails;
}

/**
 * @deprecated braintree
 */
export interface OrderPayment {
  orderId: string;
  braintreeId: string;
  nonce?: string;
  creditCardId?: string;
  paymentType: PaymentType;
  status: 'approved' | 'rejected';
  braintreeStatus?: string;
  transaction?: BraintreeTransaction;
  /** @ignore */
  refundTransactions?: BraintreeTransaction[];
  createdAt: AppDate;
}

export enum CaptureGroupStatus {
  RequiresPayment = 'requiresPayment',
  RequiresCapture = 'requiresCapture',
  Captured = 'captured',
  Canceled = 'canceled',
  Failed = 'failed',
}

export interface PaymentMethodDetails {
  type?: string;
  brand?: string;
  last4?: string;
  country?: string;
  network?: string;
  wallet?: {
    type?: string;
    dynamicLast4?: string;
  };
}

export interface Payment {
  customerId: string;
  buyerId: string;
  paymentMethodId: string;
  orderId: string;
  createdAt: AppDate;
  intentId: string;
  status:
    | 'canceled'
    | 'processing'
    | 'requires_action'
    | 'requires_capture'
    | 'requires_confirmation'
    | 'requires_payment_method'
    | 'succeeded';
  paymentType: PaymentType;
  captureGroupId?: string;
  captureGroupStatus?: CaptureGroupStatus;
  currency?: string;
  stripeClientSecret?: string | null;
  exchangeRateFromUSD?: number;
  fxQuoteId?: string;
  stripeConfirmationToken?: string;
  paymentMethodType?: string;
  paymentMethodDetails?: PaymentMethodDetails;
  creatorCurrency?: string;
  exchangeRateFromCreatorToBuyer?: number;
  exchangeRateFromPlatformToCreator?: number;
}

export interface ManualTransaction {
  sellerId: string;
  sellerProfile: Profile;
  createdById: string;
  createdByProfile: Profile;
  amount: number;
  balance: number;
  note: string;
  createdAt: AppDate;
  withdrawableBalance?: number;
}

export interface OrderTransaction {
  orderId: string;
  sellerId?: string;
  buyerId: string;
  amount: number;
  createdAt: AppDate;
  updatedAt: AppDate;
  fromBraintree: boolean;
  type:
    | 'sale'
    | 'refundBraintree'
    | 'shippingFee'
    | 'refundShippingFee'
    | 'bonus'
    | 'refundBonus'
    | 'productCoupon'
    | 'refundProductCoupon';
  status: 'pending' | 'completed' | 'canceled';
  /**
   * @deprecated
   */
  sellerBalance?: number;
  braintreeTransaction?: BraintreeTransaction;
}

export interface ShipmentParcel {
  length: string;
  width: string;
  height: string;
  distance_unit: string;
  weight: string;
  mass_unit: string;
}

/**
 * @deprecated since 12/06/22
 */
export interface OrderShipmentParcel {
  length: string;
  width: string;
  height: string;
  distance_unit: string;
  weight?: string;
  mass_unit?: string;
}

/**
 * @deprecated since 12/06/22
 */
export interface ShipmentTemplateParcel {
  distance_unit?: string;
  height?: string;
  length?: string;
  width?: string;

  weight: string;
  mass_unit: string;
  template: string;
}

export enum ShipmentOriginalParcelSource {
  Default = 'DEFAULT',
  ShowEvent = 'SHOW_EVENT',
}

export enum OrderBlockReason {
  fraud = 'fraud',
  chargebacks = 'chargebacks',
  multipleAccounts = 'multipleAccounts',
  inappropriateBehavior = 'inappropriateBehavior',
}

export interface PrivateOrderCreationBlocker {
  userProfile: Profile;
  sellerIds: string[];
  reason?: OrderBlockReason;
  createdAt: AppDate;
}

export enum OrderAddressChangeRequest {
  Pending = 'Pending',
  Approved = 'Approved',
  Rejected = 'Rejected',
  Canceled = 'Canceled',
}

export interface OrderAddressChangeHistory {
  orderId: string;
  operator: string;
  createdAt: AppDate;
  reason: string;
  rejectReason?: string;
  beforeSnapshot: Address;
  afterSnapShot: Address;
  status: OrderAddressChangeRequest;
  shipmentSnapshot?: Shipment;
  sellerTrackingInfoSnapshot?: SellerTrackingInfo;
}

export interface OrderChargeDistribution {
  commissionFeeType?: CommissionFeeType;
  commissionFeeRate: number;
  commissionFee: number;
  gatewayProcessingFeeRate: number;
  gatewayProcessingFeeExtra?: number;
  gatewayProcessingFee: number;
  sellerWithdrawableAmount: number;
  listingQuantity?: number;
  listingDiscountedSubTotal?: number;
  sellerSubtotalPaidByPopshopToSeller?: number;
  shipmentDiscountedShippingFee?: number;
  // with new ds order holding logic, this is the amount of money that has been transferred to the seller
  // note that when finalizing a ds order, the remaining amount will be `sellerWithdrawableAmount-transferredAmount`
  transferredAmount?: number;
  buyerPaidAmount: number;
  shippingSalesTaxAmount?: number;
  shippingSalesTaxRate?: number;
  listingSalesTaxAmount?: number;
  listingSalesTaxRate?: number;
  salesTaxAmount: number;
  salesTaxRate: number;
  sellerGotAmount?: number;
  couponSellerGotAmount?: number;
  listingCouponSellerGotAmount?: number;
  shippingCouponSellerGotAmount?: number;
  bonus: number;
  couponDiscountProductAmount: number;
  couponDiscountProductAmountProvidedByPopshop?: number;
  couponDiscountProductAmountProvidedBySeller?: number;
  couponDiscountShippingFeeAmount?: number;
  couponDiscountShippingFeeAmountProvidedByPopshop?: number;
  couponDiscountShippingFeeAmountProvidedBySeller?: number;
  shippingFeePayer?: ShippingFeePayer;
  popshopShippingFee?: number;
  popshopShippingFeePaidBySellerToPopshop?: number;
  popshopShippingFeePaidByPopshopToSeller?: number;
  sellerShippingFee?: number;
  sellerShippingFeePaidByPopshopToSeller?: number;
  referralGiftingDiscountSubtotal?: number;
  referralGiftingDiscountShippingFee?: number;
  referralGiftingDiscountShippingSalesTaxAmount?: number;
  referralGiftingDiscountListingSalesTaxAmount?: number;
  referralGiftingDiscountSalesTaxAmount?: number;
  // dropship
  dropshipAmounts?: Pick<
    DropshipAmounts,
    | 'costAmount'
    | 'soldAmount'
    | 'subtotalPaidByPopshop'
    | 'subtotalPaidBySeller'
    | 'managementFee'
    | 'freeShippingDiscountAmount'
    | 'shippingFee'
    | 'shippingFeePaidByPopshop'
    | 'shippingFeePaidBySeller'
    | 'supplierWithdrawableAmount'
  >;
}

export interface PackingSlip {
  created: AppDate;
  expires: AppDate;
  slipUrl: string;
}

export interface PackingSlipLineItem {
  quantity: number;
  sku?: string;
  title: string;
  total_price: number; //product price
  currency: string;
  variant_name?: string;
  coupon_discount_product_amount?: number;
  coupon_codes?: string;
  order_number: string;
  created_at: AppDate;
}

export interface PackingSlipAddress {
  name: string;
  phone: string;
  email: string;
  street1: string;
  street2: string;
  city: string;
  state: string;
  zip: string;
  country: string;
}

// /packingShips/{orderId}/
export interface PackingSlipInfo {
  to_address: PackingSlipAddress;
  from_address: PackingSlipAddress;
  placed_at: AppDate;
  order_number: string;
  line_items: PackingSlipLineItem[];
  shipping_cost: number;
  shipping_cost_currency: string;
  subtotal_price: number;
  total_price: number;
  total_tax: number;
  currency: string;
  weight: string;
  weight_unit: string;
  seller_name: string;
  buyer_name: string;
  sku?: string;
  isPickup: boolean;
}

export interface ShipmentTrackingRecord {
  createdAt: AppDate;
  orderId: string;
  sellerId: string;
  buyerId: string;
  trackingNumber?: string;
  isRemoved?: boolean;
  voided?: boolean;
  trackingUrlProvider?: string;
  carrier?: string;
  status?: string;
  transactionId?: string;
}

export enum DisputeStatus {
  Accepted = 'Accepted',
  Disputed = 'Disputed',
  Expired = 'Expired',
  Lost = 'Lost',
  Won = 'Won',
  Open = 'Open',
}

export interface DisputeStatusHistory {
  disbursementDate: string;
  effectiveDate: string;
  status: DisputeStatus;
  timestamp: string;
}

export interface Evidence {
  comment?: string;
  createdAt: string;
  id: string;
  sendToProcessorAt: string;
  url?: string;
}

export interface BraintreeDispute {
  amountDisputed: string;
  amountWon: string;
  caseNumber: string;
  createdAt: string;
  currencyIsoCode: string;
  evidence: Evidence;
  id: string;
  kind: string;
  merchantAccountId: string;
  originalDisputeId: string;
  processorComments: string;
  reason: string;
  reasonCode: string;
  reasonDescription: string;
  receivedDate: string;
  referenceNumber: string;
  replyByDate: string;
  status: DisputeStatus;
  statusHistory: DisputeStatusHistory[];
  transaction: {
    amount: string;
    createdAt: string;
    id: string;
    orderId: string;
    paymentInstrumentSubtype: string;
    purchaseOrderNumber: string;
  };
  updatedAt: string;
}

export interface BraintreeDisbursementDetails {
  disbursementDate: string;
  fundsHeld: boolean;
  settlementAmount: string;
  settlementCurrencyExchangeRate: string;
  settlementCurrencyIsoCode: string;
  success: boolean;
}

export interface OrderPriceComponentsCouponItem {
  code: string;
  id: string;
  discountedAmount: number;
  discountPublisher: 'popshop' | 'seller';
  type: CouponType;
}

export enum PopshopShippingFeeSource {
  Default = 'DEFAULT',
  // this is from readonlyProfile.adjustedPopshopShippingFee
  ReadonlyProfile = 'READONLY_PROFILE',
  ReadonlyProfileShippingPrice = 'READONLY_PROFILE_SHIPPING_PRICE',
  ShowEvent = 'SHOW_EVENT',
  Config = 'CONFIG',
  /**
   * @deprecated
   */
  ShippoPlatform = 'SHIPPO_PLATFORM',
  PackageTemplate = 'PACKAGE_TEMPLATE',
}

export enum SellerShippingFeeSource {
  Store = 'STORE',
  Listing = 'LISTING',
  PackageTemplate = 'PACKAGE_TEMPLATE',
}

export enum ListingPriceSource {
  Listing = 'LISTING',
  Variant = 'VARIANT',
}

export enum ProcessingFeePayer {
  Seller = 'SELLER',
  Popshop = 'POPSHOP',
}

export type RefundDetail = Partial<
  Pick<
    OrderPriceComponents,
    | 'listingDiscountedSubTotal'
    | 'shipmentDiscountedShippingFee'
    | 'salesTaxAmount'
  >
> & {
  subtotal: number;
  listingQuantity?: number;
  deductProcessingFee: number;
  processingFeePayer: ProcessingFeePayer;
};

export enum RefundType {
  ALL = 'all',
  SHIPPING_FEE = 'shippingFee',
  LISTING = 'listing',
  CUSTOM_AMOUNT = 'customAmount',
  CANCEL = 'cancel',
  CANCEL_LISTING = 'cancelListing',
}

export interface RefundFeeTransaction {
  stripeAccountRefundFee: number;
  isReturned?: boolean;
  updatedAt: AppDate;
}

export type RefundRecord = {
  /**
   * detail including:
   * 1. total
   * 2. listing subtotal
   * 3. listing quantity
   * 4. shipping fee
   * 5. sales tax amount
   * 6. gateway processing fee
   * 7. gateway processing fee payer
   */
  detail: RefundDetail;
  types: RefundType[];
  shouldRestock?: boolean;
  shouldFullyRefund?: boolean;
  shouldSubtotalFullyRefund?: boolean;
  shouldShippingFeeFullyRefund?: boolean;
  reason?: string;
  /**
   * if order refund, stripe main account transfer fee back to connect account
   */
  refundFeeTransaction?: RefundFeeTransaction;
  dropshipSupplier?: DropshipSupplier & { id: string };
} & ModelBase;

export type StripeRefund = {
  id: string;
  object: 'refund';
  amount: number;
  balance_transaction: string | null;
  charge: string | null;
  created: number;
  currency: string;
  description?: string;
  failure_balance_transaction?: string;
  failure_reason?: string;
  instructions_email?: string;
  payment_intent: string | null;
  reason: string | null;
  receipt_number: string | null;
  source_transfer_reversal: string;
  status: string | null;
};

export type RefundRecordDetail = {
  intent: StripeRefund | BraintreeTransaction;
  operatorId: string;
  operator: Profile | null;
  quantity?: number;
  listingSnapshot: Listing;
  listingId: string;
  variantId?: string | null;
  sellerId: string;
  sellerAmountDistribution: {
    // amount to refund to buyer
    returnCommissionFee: number; // commission fee to refund to buyer
    orderReturnGatewayProcessingFee: number; // gateway processing fee to refund to buyer from seller
    orderDeductSellerGotWithdrawableAmount: number; // seller withrawable amount to refund to buyer from seller
    // amount to deduct from PSL
    returnGatewayProcessingFee: number; // gateway processing fee to refund to buyer from PSL, zero if seller pays
    // amount to deduct from seller
    deductShippingFee: number; // deduct shipping fee from seller
    deductSellerGotWithdrawableAmount: number; // seller withrawable amount(plus gateway processing fee if seller pays) to refund to buyer from seller
    deductBalanceByCoupon?: number; // = deductCouponSellerGotAmount
    deductCouponSellerGotAmount?: number; // deduct PSL provided listing&shipping coupon amount from seller
    deductItemCouponAmount: number; // deduct PSL provided listing coupon amount from seller
    deductShippingCouponSellerGotAmount?: number; // deduct PSL provided shipping coupon amount from seller
    deductSellerSubtotalPaidByPopshopToSeller?: number;
    deductPopshopShippingFee?: number;
    deductPopshopShippingFeePaidBySellerToPopshop?: number;
    deductPopshopShippingFeePaidByPopshopToSeller?: number;
    deductSellerShippingFee?: number;
    deductSellerShippingFeePaidByPopshopToSeller?: number;
    // amount after this refund
    commissionFee: number; // commission fee after this refund
    orderGatewayProcessingFee: number; // gateway processing fee after this refund
    orderSellerGotWithdrawableAmount: number; // seller withrawable amount after this refund
    couponSellerGotAmount?: number;
    listingCouponSellerGotAmount?: number;
    shippingCouponSellerGotAmount?: number;
    sellerSubtotalPaidByPopshopToSeller?: number;
    popshopShippingFee?: number;
    popshopShippingFeePaidBySellerToPopshop?: number;
    popshopShippingFeePaidByPopshopToSeller?: number;
    sellerShippingFee?: number;
    sellerShippingFeePaidByPopshopToSeller?: number;
  };
  dropshipSupplierAmountDistribution?: {
    // amount to deduct from dropship supplier
    deductCostAmount: number;
    deductSoldAmount: number;
    deductSubtotalPaidByPopshop?: number;
    deductSubtotalPaidBySeller?: number;
    deductManagementFee: number;
    deductFreeShippingDiscountAmount?: number;
    deductShippingFee: number;
    deductShippingFeePaidByPopshop?: number;
    deductShippingFeePaidBySeller?: number;
    deductSupplierWithdrawableAmount: number;
    // amount after this refund
    costAmount: number;
    soldAmount: number;
    subtotalPaidByPopshop?: number;
    subtotalPaidBySeller?: number;
    managementFee: number;
    freeShippingDiscountAmount?: number;
    shippingFee: number;
    shippingFeePaidByPopshop?: number;
    shippingFeePaidBySeller?: number;
    supplierWithdrawableAmount: number;
  };
} & RefundRecord;

/**
 * @deprecated since 12/06/22
 */
export type ShippingRate = {
  id: string;
  attributes: string[];
  duration_terms: string;
  estimated_days: number;
  provider_image_75: string;
  provider_image_200: string;
  servicelevel: {
    name: string;
    terms: string;
    token: string;
  };
  cost: {
    currency: string;
    base: number;
    margin: number;
    total: number;
  };
  test: boolean;
};

export type ShippingCost =
  | {
      status: 'pending' | 'failed';
      message?: string;
    }
  | {
      status: 'success';
      message?: string;
      shippoShipmentId?: string;
      shippoRateId?: string;
      shippoRate?: ShippoRate;
      /**
       * @deprecated
       */
      shipmentId?: string;
      /**
       * @deprecated
       */
      rates?: ShippingRate[];
    };

export enum SalesTaxRecipient {
  Seller = 'seller',
  Popshop = 'popshop',
}

export interface CommentSoldSalesTaxCalcResultDetail {
  tax_name: string;
  amount: number; // in cents
  rate: number;
  flat_rate: boolean;
}

export interface CommentSoldSalesTaxCalcResultLine {
  sku: string;
  tax_amount: number; // in cents
  taxable_amount: number; // in cents
  details: CommentSoldSalesTaxCalcResultDetail[];
}

export interface CommentSoldSalesTaxCalcResult {
  tax_total: number; // in cents
  taxable_amount: number; // in cents
  details: CommentSoldSalesTaxCalcResultDetail[];
  lines: CommentSoldSalesTaxCalcResultLine[];
}

export interface AvalaraSalesTaxCalcResult {
  id: string; // avalara id
  totalAmount: number; // dollar
  totalTax: number; // dollar
  totalTaxable: number; // dollar
  totalExempt: number; // dollar
  status: string;
  code?: string; // avalara code
}

export interface OrderPriceComponents {
  shippingFeeTaxable?: boolean;
  commentSoldSalesTaxCalcResult?:
    | CommentSoldSalesTaxCalcResult
    | AvalaraSalesTaxCalcResult;
  commentSoldSalesTaxCalcError?: string;
  shippingSalesTaxAmount?: number;
  shippingSalesTaxRate?: number;
  listingSalesTaxAmount?: number;
  listingSalesTaxRate?: number;
  salesTaxAmount: number;
  salesTaxRate: number;
  salesTaxRecipient?: SalesTaxRecipient;
  buyerPaidAmount: number;
  sellerGotAmount: number;
  total: number;
  sellerGotWithdrawableAmount: number;
  listingPlatformCost?: number;
  listingPrice: number;
  listingOriginPrice?: number;
  listingPriceSource?: ListingPriceSource;
  listingQuantity: number;
  listingDiscountedSubTotal: number;
  sellerSubtotalPaidByPopshopToSeller?: number;
  commissionFeeType?: CommissionFeeType;
  commissionFeeRate: number;
  eventExtraCommissionFeeRate?: number;
  baseCommissionFeeRate: number;
  commissionFee: number;
  gatewayProcessingFeeRate: number;
  gatewayProcessingFeeExtra?: number;
  gatewayProcessingFee: number;
  shipmentDiscountedShippingFee: number;
  shipmentOriginShippingFee: number;
  shipmentShippingMethod: OrderShippingMethod;
  shipmentShippingFeePayer: ShippingFeePayer;
  couponDiscountProductAmount: number;
  couponDiscountProductAmountProvidedByPopshop?: number;
  couponDiscountProductAmountProvidedBySeller?: number;
  couponDiscountShippingFeeAmount: number;
  couponDiscountShippingFeeAmountProvidedByPopshop?: number;
  couponDiscountShippingFeeAmountProvidedBySeller?: number;
  coupons?: OrderPriceComponentsCouponItem[];
  paymentProvider: 'braintree' | 'stripe';
  couponSellerGotAmount: number;
  listingCouponSellerGotAmount?: number;
  shippingCouponSellerGotAmount?: number;
  popshopShippingFee?: number;
  popshopShippingFeeSource?: PopshopShippingFeeSource;
  popshopShippingFeePaidBySellerToPopshop?: number;
  popshopShippingFeePaidByPopshopToSeller?: number;
  shippingIntegrationType?: ShippingIntegrationType;
  useArrangeMyOwnShippingV2?: boolean;
  shippingMarginRate?: number;
  /**
   * @deprecated since 12/06/22
   */
  defaultShippingCost?: ShippingCost;
  /**
   * @deprecated since 12/06/22
   */
  userSelectedShippingCost?: ShippingCost;
  shippingSurcharge?: number;
  sellerShippingFee?: number;
  sellerShippingFeeSource?: SellerShippingFeeSource;
  sellerShippingFeePaidByPopshopToSeller?: number;
  referralGiftingDiscountSubtotal?: number;
  referralGiftingDiscountShippingFee?: number;
  referralGiftingDiscountShippingSalesTaxAmount?: number;
  referralGiftingDiscountListingSalesTaxAmount?: number;
  referralGiftingDiscountSalesTaxAmount?: number;
  dropshipAmounts?: DropshipAmounts;
  currency?: string;
  subtotal?: number;
}

export interface TippingOrderPriceComponents {
  total: number;
  // currently this is the same as total
  tipperPaidAmount: number;

  recipientGotAmount: number;
  recipientGotWithdrawableAmount: number;

  // defaults to 0.03
  gatewayProcessingFeeRate: number;
  gatewayProcessingFee: number;

  // defaults to 0
  commissionFeeRate: number;
  commissionFee: number;
}

// /tippingOrders/{autoId}
export interface TippingOrder extends ModelBase {
  orderNumber: string;
  priceComponents: TippingOrderPriceComponents;

  tipId: string;
  tip: Tip;

  tipperId: string;
  tipperProfile: Profile;
  recipientId: string;
  recipientProfile: Profile;

  source:
    | { type: 'liveStream'; liveStreamId: string; liveStream: LiveStream }
    | {
        type: 'upcomingShow';
        upcomingShowId: string;
        upcomingShow: UpcomingShow;
      };

  payment?: Payment;
  userAgent?: string;
}
